import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import type {
  MannequinMetadata,
  MannequinOutputSurface,
  MannequinSurfaceResponse,
} from "../api/mannequin";
export type MannequinViewMode = "single" | "all";
type Props = {
  metadata: MannequinMetadata;
  outputs: string[];
  surface: MannequinSurfaceResponse;
  viewMode: MannequinViewMode;
};
type ViewState = {
  yaw: number;
  pitch: number;
  zoom: number;
  panX: number;
  panY: number;
};
type Panel = {
  output: string;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  group: THREE.Group;
};
const LAYOUT = { width: 2.55, depth: 1.62, lift: 1.34 };
const DEFAULT_VIEW: ViewState = { yaw: -0.38, pitch: 0.78, zoom: 1, panX: 0, panY: 0 };
function formatNumber(value: number | undefined): string {
  if (value === undefined || Number.isNaN(value)) return "n/a";
  return Math.abs(value) >= 100 ? value.toFixed(1) : value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}
function normValue(value: number, min: number, max: number): number {
  return Math.max(0, Math.min(1, (value - min) / Math.max(1.0e-12, max - min)));
}
function zForValue(value: number, range: [number, number]): number {
  return (normValue(value, range[0], range[1]) - 0.5) * LAYOUT.lift;
}
function fractionInRange(value: number, min: number, max: number): number {
  return Math.max(0, Math.min(1, (value - min) / Math.max(1.0e-12, max - min)));
}
function finiteOrFallback(value: number, fallback: number): number {
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}
function sequentialColor(t: number): THREE.Color {
  const stops: Array<[number, [number, number, number]]> = [
    [0, [36, 73, 158]],
    [0.23, [31, 129, 184]],
    [0.48, [24, 170, 139]],
    [0.72, [178, 204, 73]],
    [1, [226, 118, 43]],
  ];
  const clamped = Math.max(0, Math.min(1, t));
  for (let index = 0; index < stops.length - 1; index += 1) {
    const [a, ca] = stops[index]!;
    const [b, cb] = stops[index + 1]!;
    if (clamped <= b) {
      const local = (clamped - a) / Math.max(1.0e-12, b - a);
      return new THREE.Color(
        (ca[0] + (cb[0] - ca[0]) * local) / 255,
        (ca[1] + (cb[1] - ca[1]) * local) / 255,
        (ca[2] + (cb[2] - ca[2]) * local) / 255,
      );
    }
  }
  const last = stops[stops.length - 1]![1];
  return new THREE.Color(last[0] / 255, last[1] / 255, last[2] / 255);
}
function makeSurfaceGeometry(z: number[][], zRange: [number, number]): THREE.BufferGeometry {
  const rows = z.length;
  const cols = z[0]?.length ?? 0;
  const positions: number[] = [];
  const colors: number[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const value = finiteOrFallback(z[row]?.[col] ?? zRange[0], zRange[0]);
      const color = sequentialColor(normValue(value, zRange[0], zRange[1]));
      positions.push(
        -LAYOUT.width / 2 + (col / Math.max(1, cols - 1)) * LAYOUT.width,
        -LAYOUT.depth / 2 + (row / Math.max(1, rows - 1)) * LAYOUT.depth,
        zForValue(value, zRange),
      );
      colors.push(color.r, color.g, color.b);
    }
  }
  const indices: number[] = [];
  for (let row = 0; row < rows - 1; row += 1) {
    for (let col = 0; col < cols - 1; col += 1) {
      const a = row * cols + col;
      const b = a + 1;
      const c = a + cols;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}
function addSurfaceMesh(group: THREE.Group, surface: MannequinOutputSurface): void {
  group.add(
    new THREE.Mesh(
      makeSurfaceGeometry(surface.z, surface.z_range),
      new THREE.MeshStandardMaterial({
        vertexColors: true,
        roughness: 0.76,
        metalness: 0,
        side: THREE.DoubleSide,
      }),
    ),
  );
}
function addWireGrid(group: THREE.Group, surface: MannequinOutputSurface): void {
  const rows = surface.z.length;
  const cols = surface.z[0]?.length ?? 0;
  const material = new THREE.LineBasicMaterial({ color: "#2b3b38", transparent: true, opacity: 0.62 });
  const makePoint = (col: number, row: number) => new THREE.Vector3(
    -LAYOUT.width / 2 + (col / Math.max(1, cols - 1)) * LAYOUT.width,
    -LAYOUT.depth / 2 + (row / Math.max(1, rows - 1)) * LAYOUT.depth,
    zForValue(finiteOrFallback(surface.z[row]?.[col] ?? surface.z_range[0], surface.z_range[0]), surface.z_range) + 0.008,
  );
  const rowStep = Math.max(1, Math.floor(rows / 9));
  const colStep = Math.max(1, Math.floor(cols / 9));
  for (let row = 0; row < rows; row += rowStep) {
    group.add(new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(Array.from({ length: cols }, (_, col) => makePoint(col, row))),
      material.clone(),
    ));
  }
  for (let col = 0; col < cols; col += colStep) {
    group.add(new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(Array.from({ length: rows }, (_, row) => makePoint(col, row))),
      material.clone(),
    ));
  }
}
function makeTextSprite(text: string, panelCount: number, tone: "axis" | "tick" = "tick"): THREE.Sprite {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return new THREE.Sprite(new THREE.SpriteMaterial({ color: "#b9c7be" }));
  const fontSize = 40;
  context.font = `700 ${fontSize}px Inter, system-ui, sans-serif`;
  const metrics = context.measureText(text);
  canvas.width = Math.ceil(metrics.width + 36);
  canvas.height = fontSize + 20;
  context.font = `700 ${fontSize}px Inter, system-ui, sans-serif`;
  context.textBaseline = "middle";
  context.textAlign = "center";
  context.fillStyle = tone === "axis" ? "rgba(28, 47, 65, 0.82)" : "rgba(20, 37, 61, 0.86)";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = tone === "axis" ? "#d9f1ff" : "#d8e5ff";
  context.fillText(text, canvas.width / 2, canvas.height / 2);
  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  }));
  const height = panelCount > 1 ? 0.105 : 0.13;
  sprite.scale.set(height * (canvas.width / canvas.height), height, 1);
  sprite.renderOrder = 20;
  return sprite;
}
function addLine(group: THREE.Group, points: THREE.Vector3[], color: string, opacity = 0.86): void {
  group.add(new THREE.Line(
    new THREE.BufferGeometry().setFromPoints(points),
    new THREE.LineBasicMaterial({ color, transparent: opacity < 1, opacity, depthTest: false }),
  ));
}
function axisTicks(min: number, max: number) {
  return [
    { fraction: 0, label: formatNumber(min) },
    { fraction: 0.5, label: formatNumber((min + max) / 2) },
    { fraction: 1, label: formatNumber(max) },
  ];
}
function addAxisFrame(
  group: THREE.Group,
  data: MannequinSurfaceResponse,
  output: string,
  zRange: [number, number],
  metadata: MannequinMetadata,
  panelCount: number,
): void {
  const w = LAYOUT.width;
  const d = LAYOUT.depth;
  const zBase = -LAYOUT.lift / 2 - 0.06;
  const zTop = LAYOUT.lift / 2 + 0.06;
  const x0 = -w / 2;
  const y0 = -d / 2;
  addLine(group, [new THREE.Vector3(x0, y0, zBase), new THREE.Vector3(w / 2, y0, zBase)], "#72807d");
  addLine(group, [new THREE.Vector3(x0, y0, zBase), new THREE.Vector3(x0, d / 2, zBase)], "#72807d");
  addLine(group, [new THREE.Vector3(x0, y0, zBase), new THREE.Vector3(x0, y0, zTop)], "#84908d");
  const tick = panelCount > 1 ? 0.045 : 0.065;
  for (const item of axisTicks(data.x_range[0], data.x_range[1])) {
    const x = x0 + item.fraction * w;
    addLine(group, [new THREE.Vector3(x, y0 - tick, zBase), new THREE.Vector3(x, y0 + tick, zBase)], "#63706d", 0.7);
  }
  for (const item of axisTicks(data.y_range[0], data.y_range[1])) {
    const y = y0 + item.fraction * d;
    addLine(group, [new THREE.Vector3(x0 - tick, y, zBase), new THREE.Vector3(x0 + tick, y, zBase)], "#63706d", 0.7);
  }
  for (const item of axisTicks(zRange[0], zRange[1])) {
    const z = zBase + item.fraction * (zTop - zBase);
    addLine(group, [new THREE.Vector3(x0 - tick, y0, z), new THREE.Vector3(x0 + tick, y0, z)], "#63706d", 0.72);
  }
  const xUnit = metadata.input_config[data.x_axis]?.unit;
  const yUnit = metadata.input_config[data.y_axis]?.unit;
  const labels: Array<[string, THREE.Vector3, "axis" | "tick"]> = [
    [`${data.x_axis}${xUnit ? ` (${xUnit})` : ""}`, new THREE.Vector3(0, y0 - 0.31, zBase), "axis"],
    [`${data.y_axis}${yUnit ? ` (${yUnit})` : ""}`, new THREE.Vector3(x0 - 0.42, 0, zBase), "axis"],
    [output, new THREE.Vector3(x0 - 0.28, y0 - 0.04, zTop + 0.06), "axis"],
  ];
  for (const [text, position, tone] of labels) {
    const sprite = makeTextSprite(text, panelCount, tone);
    sprite.position.copy(position);
    group.add(sprite);
  }
}
function addZeroPlane(group: THREE.Group, zRange: [number, number]): void {
  if (zRange[0] > 0 || zRange[1] < 0) return;
  const zero = new THREE.Mesh(
    new THREE.PlaneGeometry(LAYOUT.width * 1.03, LAYOUT.depth * 1.15),
    new THREE.MeshBasicMaterial({
      color: "#b8c2cb",
      transparent: true,
      opacity: 0.22,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  );
  zero.position.z = zForValue(0, zRange) + 0.004;
  zero.renderOrder = 2;
  group.add(zero);
}
function surfacePoint(data: MannequinSurfaceResponse, x: number, y: number, z: number, zRange: [number, number]) {
  return new THREE.Vector3(
    -LAYOUT.width / 2 + fractionInRange(x, data.x_range[0], data.x_range[1]) * LAYOUT.width,
    -LAYOUT.depth / 2 + fractionInRange(y, data.y_range[0], data.y_range[1]) * LAYOUT.depth,
    zForValue(finiteOrFallback(z, zRange[0]), zRange),
  );
}
function addOverlays(group: THREE.Group, data: MannequinSurfaceResponse, surface: MannequinOutputSurface): void {
  for (const overlay of surface.overlays) {
    if (overlay.mode === "lines") {
      const points = overlay.x.map((x, index) => surfacePoint(data, x, overlay.y[index]!, overlay.z[index]!, surface.z_range));
      group.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(points),
        new THREE.LineBasicMaterial({ color: overlay.line?.color ?? "#f4b14a", transparent: true, opacity: 0.94 }),
      ));
    } else {
      const color = new THREE.Color(overlay.marker?.color ?? "#50c8b8");
      const radius = overlay.marker?.size ? Math.max(0.035, overlay.marker.size * 0.008) : 0.055;
      for (let index = 0; index < overlay.x.length; index += 1) {
        const marker = new THREE.Mesh(
          new THREE.SphereGeometry(radius, 14, 8),
          new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.96 }),
        );
        marker.position.copy(surfacePoint(data, overlay.x[index]!, overlay.y[index]!, overlay.z[index]!, surface.z_range));
        marker.position.z += 0.06;
        group.add(marker);
      }
    }
  }
}
function applyView(panel: Panel, view: ViewState, panelCount: number): void {
  panel.group.rotation.set(view.pitch - 0.34, 0, view.yaw);
  panel.group.position.set(view.panX * 0.007, -view.panY * 0.007, 0);
  panel.group.scale.setScalar(view.zoom * (panelCount > 1 ? 0.66 : 0.9));
}
function clampView(view: ViewState): ViewState {
  return {
    yaw: Math.max(-Math.PI, Math.min(Math.PI, view.yaw)),
    pitch: Math.max(0.18, Math.min(1.35, view.pitch)),
    zoom: Math.max(0.48, Math.min(3.2, view.zoom)),
    panX: Math.max(-80, Math.min(80, view.panX)),
    panY: Math.max(-68, Math.min(68, view.panY)),
  };
}
function disposeScene(scene: THREE.Scene): void {
  scene.traverse((object) => {
    const mesh = object as any;
    if (mesh.geometry) mesh.geometry.dispose();
    const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
    for (const material of materials) {
      if (material.map) material.map.dispose();
      material.dispose();
    }
  });
  scene.clear();
}
function createPanel(
  data: MannequinSurfaceResponse,
  output: string,
  metadata: MannequinMetadata,
  panelCount: number,
): Panel | null {
  const outputSurface = data.output_surfaces[output];
  if (!outputSurface) return null;
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(42, 1, 0.05, 80);
  camera.up.set(0, 0, 1);
  camera.position.set(0, -3.72, 2.42);
  camera.lookAt(0, 0, 0.1);
  scene.add(new THREE.HemisphereLight("#daeef4", "#1d2724", 1.62));
  const keyLight = new THREE.DirectionalLight("#ffffff", 1.7);
  keyLight.position.set(-2.4, -3.2, 4.2);
  scene.add(keyLight);
  const group = new THREE.Group();
  scene.add(group);
  const base = new THREE.Mesh(
    new THREE.PlaneGeometry(LAYOUT.width * 1.04, LAYOUT.depth * 1.16),
    new THREE.MeshBasicMaterial({ color: "#5d6670", transparent: true, opacity: 0.34, side: THREE.DoubleSide }),
  );
  base.position.z = -LAYOUT.lift / 2 - 0.12;
  group.add(base);
  addAxisFrame(group, data, output, outputSurface.z_range, metadata, panelCount);
  addZeroPlane(group, outputSurface.z_range);
  addSurfaceMesh(group, outputSurface);
  addWireGrid(group, outputSurface);
  addOverlays(group, data, outputSurface);
  const panel = { output, scene, camera, group };
  applyView(panel, DEFAULT_VIEW, panelCount);
  return panel;
}
export default function MannequinSurfaceGrid({ metadata, outputs, surface, viewMode }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const panelsRef = useRef<Panel[]>([]);
  const viewRef = useRef<ViewState>({ ...DEFAULT_VIEW });
  const dragRef = useRef<{ x: number; y: number; view: ViewState; mode: "rotate" | "pan" } | null>(null);
  const renderRef = useRef<() => void>(() => {});
  const outputSignature = useMemo(() => outputs.join("|"), [outputs]);
  const renderOutputs = useMemo(() => outputs, [outputSignature]);
  useEffect(() => {
    if (!canvasRef.current) return undefined;
    const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, alpha: true, antialias: true });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setScissorTest(true);
    rendererRef.current = renderer;
    const resizeObserver = new ResizeObserver(() => renderRef.current());
    if (rootRef.current) resizeObserver.observe(rootRef.current);
    return () => {
      resizeObserver.disconnect();
      renderer.dispose();
      rendererRef.current = null;
    };
  }, []);
  useEffect(() => {
    const panels = renderOutputs
      .map((output) => createPanel(surface, output, metadata, outputs.length))
      .filter((panel): panel is Panel => Boolean(panel));
    panelsRef.current = panels;
    const render = () => {
      const renderer = rendererRef.current;
      const root = rootRef.current;
      if (!renderer || !root) return;
      const rootRect = root.getBoundingClientRect();
      const width = Math.max(1, Math.floor(rootRect.width));
      const height = Math.max(1, Math.floor(rootRect.height));
      renderer.setSize(width, height, false);
      renderer.setScissorTest(false);
      renderer.setViewport(0, 0, width, height);
      renderer.clear(true, true, true);
      renderer.setScissorTest(true);
      for (const panel of panelsRef.current) {
        const tile = root.querySelector<HTMLElement>(`[data-output="${panel.output}"]`);
        if (!tile) continue;
        const rect = tile.getBoundingClientRect();
        if (rect.width < 3 || rect.height < 3) continue;
        const vx = Math.max(0, Math.floor(rect.left - rootRect.left));
        const vy = Math.max(0, Math.floor(height - (rect.bottom - rootRect.top)));
        const vw = Math.max(1, Math.floor(rect.width));
        const vh = Math.max(1, Math.floor(rect.height));
        renderer.setViewport(vx, vy, vw, vh);
        renderer.setScissor(vx, vy, vw, vh);
        panel.camera.aspect = vw / vh;
        panel.camera.updateProjectionMatrix();
        applyView(panel, viewRef.current, panelsRef.current.length);
        renderer.render(panel.scene, panel.camera);
      }
    };
    renderRef.current = render;
    const frame = window.requestAnimationFrame(render);
    return () => {
      window.cancelAnimationFrame(frame);
      if (panelsRef.current === panels) panelsRef.current = [];
      panels.forEach((panel) => disposeScene(panel.scene));
    };
  }, [metadata, outputSignature, outputs.length, renderOutputs, surface]);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      event.preventDefault();
      root.setPointerCapture(event.pointerId);
      dragRef.current = {
        x: event.clientX,
        y: event.clientY,
        view: { ...viewRef.current },
        mode: event.shiftKey || event.button === 2 ? "pan" : "rotate",
      };
    };
    const onPointerMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      viewRef.current = drag.mode === "pan"
        ? clampView({ ...drag.view, panX: drag.view.panX + dx * 0.62, panY: drag.view.panY + dy * 0.62 })
        : clampView({ ...drag.view, yaw: drag.view.yaw + dx * 0.01, pitch: drag.view.pitch + dy * 0.008 });
      renderRef.current();
    };
    const finishDrag = (event: PointerEvent) => {
      dragRef.current = null;
      if (root.hasPointerCapture(event.pointerId)) root.releasePointerCapture(event.pointerId);
    };
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      viewRef.current = clampView({ ...viewRef.current, zoom: viewRef.current.zoom * Math.exp(-event.deltaY * 0.0012) });
      renderRef.current();
    };
    const reset = () => {
      viewRef.current = { ...DEFAULT_VIEW };
      renderRef.current();
    };
    const preventMenu = (event: MouseEvent) => event.preventDefault();
    root.addEventListener("pointerdown", onPointerDown);
    root.addEventListener("pointermove", onPointerMove);
    root.addEventListener("pointerup", finishDrag);
    root.addEventListener("pointercancel", finishDrag);
    root.addEventListener("wheel", onWheel, { passive: false });
    root.addEventListener("dblclick", reset);
    root.addEventListener("contextmenu", preventMenu);
    return () => {
      root.removeEventListener("pointerdown", onPointerDown);
      root.removeEventListener("pointermove", onPointerMove);
      root.removeEventListener("pointerup", finishDrag);
      root.removeEventListener("pointercancel", finishDrag);
      root.removeEventListener("wheel", onWheel);
      root.removeEventListener("dblclick", reset);
      root.removeEventListener("contextmenu", preventMenu);
    };
  }, []);
  return (
    <div
      className={`three-surface-root ${viewMode}`}
      ref={rootRef}
      data-testid="mannequin-three-root"
      title="Drag to rotate. Shift/right-drag to pan. Wheel to zoom. Double-click to reset view."
    >
      <canvas className="three-surface-canvas" ref={canvasRef} />
      <div className={`three-panel-overlays ${viewMode}`}>
        {outputs.map((output) => {
          const outputSurface = surface.output_surfaces[output];
          return (
            <article className={`three-surface-panel ${viewMode}`} data-output={output} key={output}>
              <div className="three-panel-header">
                <strong>{output}</strong>
                <span>{metadata.output_labels[output] ?? output}</span>
              </div>
              <div className="three-panel-footer">
                <span>{formatNumber(outputSurface?.z_range[0])}</span>
                <span>mean {formatNumber(outputSurface?.stats.mean)}</span>
                <span>{formatNumber(outputSurface?.z_range[1])}</span>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
